import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { Plus, FilePdf, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import EntityPicker from "./EntityPicker";
import { downloadBlob } from "./PdfBrandingDialog";
import { fmtDate } from "../../lib/dateUtil";

const STATUS_CLASS = { draft: "bg-slate-100 text-slate-700", issued: "bg-emerald-100 text-emerald-700", superseded: "bg-red-100 text-red-700" };

function reviewFlag(reviewDate) {
  if (!reviewDate) return null;
  const days = Math.ceil((new Date(reviewDate) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  if (days > 14) return null;
  const over = days < 0;
  return <Badge className={`ml-1 text-[10px] ${over ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`} data-testid="rams-review-flag">{over ? `review ${-days}d overdue` : days === 0 ? "review today" : `review in ${days}d`}</Badge>;
}

/** RAMS register: search/filter, create blank or from a job, open editor, quick PDF, delete drafts. */
export default function RamsListPanel({ entityType, entityId, embedded = false }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    API.get("/hs/rams", { params: { status: status || undefined, search: search || undefined, entity_type: entityType, entity_id: entityId } })
      .then(({ data }) => setRows(data.rams || [])).catch(() => {}).finally(() => setLoading(false));
  }, [status, search, entityType, entityId]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const create = async (pick) => {
    try {
      const { data } = await API.post("/hs/rams", pick ? { entity_type: pick.entity_type, entity_id: pick.entity_id } : (entityType && entityId ? { entity_type: entityType, entity_id: entityId } : {}));
      toast.success(`${data.reference} created`);
      navigate(`/health-safety/rams/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete draft ${r.reference}?`)) return;
    try { await API.delete(`/hs/rams/${r.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const pdf = async (r) => {
    try { const resp = await API.get(`/hs/rams/${r.id}/pdf`, { responseType: "blob" }); downloadBlob(resp.data, `${r.reference}_v${r.version}.pdf`); } catch { toast.error("PDF failed"); }
  };

  return (
    <div className="space-y-3" data-testid="rams-list-panel">
      <div className="flex flex-wrap items-center gap-2">
        {!embedded && (
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search reference, title, client, site…" className="pl-8 h-8 text-xs" data-testid="rams-search" />
          </div>
        )}
        <div className="flex gap-1">
          {["", "draft", "issued", "superseded"].map(s => (
            <button key={s} onClick={() => setStatus(s)} className={`text-xs px-2.5 py-1 rounded-full border ${status === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid={`rams-filter-${s || "all"}`}>{s ? s[0].toUpperCase() + s.slice(1) : "All"}</button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          {entityType && entityId ? <Button size="sm" onClick={() => create(null)} data-testid="rams-new-from-entity"><Plus size={14} className="mr-1" /> RAMS for this job</Button> : (
            <>
              <Button variant="outline" size="sm" onClick={() => create(null)} data-testid="rams-new-blank"><Plus size={14} className="mr-1" /> Blank RAMS</Button>
              <Button size="sm" onClick={() => setShowNew(true)} data-testid="rams-new-from-job"><Plus size={14} className="mr-1" /> RAMS from job</Button>
            </>
          )}
        </div>
      </div>

      {loading && rows.length === 0 ? <p className="text-xs text-muted-foreground p-4">Loading…</p> : rows.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground" data-testid="rams-empty">No RAMS yet. Create one from a job to pull in the client, site, scope and crew automatically.</div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-[10px] uppercase text-muted-foreground"><tr><th className="text-left p-2">Reference</th><th className="text-left p-2">Title</th><th className="text-left p-2 hidden md:table-cell">Client / site</th><th className="text-left p-2">Status</th><th className="text-left p-2 hidden md:table-cell">Updated</th><th className="p-2"></th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/40 cursor-pointer" onClick={() => navigate(`/health-safety/rams/${r.id}`)} data-testid={`rams-row-${r.id}`}>
                  <td className="p-2 font-semibold text-primary whitespace-nowrap">{r.reference}<span className="text-muted-foreground font-normal"> v{r.version}</span></td>
                  <td className="p-2">{r.title}</td>
                  <td className="p-2 hidden md:table-cell text-muted-foreground">{[r.job?.client_name, r.job?.property_address].filter(Boolean).join(" · ")}</td>
                  <td className="p-2"><Badge className={`text-[10px] ${STATUS_CLASS[r.status]}`}>{r.status}</Badge>{r.status === "issued" && <span className="ml-1 text-[10px] text-muted-foreground">{(r.operatives_briefed || []).length}/{(r.operatives || []).length} briefed</span>}{r.status === "issued" && reviewFlag(r.review_date)}</td>
                  <td className="p-2 hidden md:table-cell text-muted-foreground">{fmtDate(r.updated_at)}</td>
                  <td className="p-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Download PDF" onClick={() => pdf(r)} data-testid={`rams-row-pdf-${r.id}`}><FilePdf size={14} /></Button>
                    {r.status === "draft" && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" onClick={() => remove(r)} data-testid={`rams-row-delete-${r.id}`}><Trash size={14} /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-lg" data-testid="rams-new-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">New RAMS from a job</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Pick the job, emergency job or job request — client, site, scope, dates and crew are pulled in for you.</p>
          <EntityPicker onPick={(p) => { setShowNew(false); create(p); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
